// ---------------------------------------------------------------------------
// Email editor type detection
//
// A marketing record (template / campaign / automation step) stores its email
// body as `html_content` plus an optional `json_content`. When `json_content`
// holds a valid DnD email model the record was authored with the drag-and-drop
// editor; otherwise it is a plain-HTML record edited with the HTML editor.
// ---------------------------------------------------------------------------

import { migrateModel } from '@/components/dnd-editor-v2';
import type { DndEmailModel } from '@/components/dnd-editor-v2';

export type EmailEditorType = 'html' | 'dnd';

/**
 * Parse a persisted `jsonContent` string into a DnD model, or return null when
 * it isn't a DnD document. Guards against empty / arbitrary JSON so plain-HTML
 * records (jsonContent of null / '' / 'null' / non-model objects) fall through
 * to the HTML editor.
 */
export function parseDndModel(json: string | null | undefined): DndEmailModel | null {
  if (typeof json !== 'string') return null;
  const trimmed = json.trim();
  if (!trimmed || trimmed === 'null') return null;
  try {
    const raw = JSON.parse(trimmed);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    if (!Array.isArray((raw as Record<string, unknown>).layers)) return null;
    return migrateModel(raw as Record<string, unknown>);
  } catch {
    return null;
  }
}

/** Which editor a stored record should open in, based on its jsonContent. */
export function detectEditorType(json: string | null | undefined): EmailEditorType {
  return parseDndModel(json) ? 'dnd' : 'html';
}
